/**
 * Плагины для графика Chart.js
 */

import { Chart } from 'chart.js';
import ChartjsPluginWatermark from 'chartjs-plugin-watermark';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// кнопка Показать все/Скрыть все
const HideAll = {
    id: 'hideAll',
    beforeDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        // Координаты кнопки
        const x = chartArea.right - 90;
        const y = chartArea.top;

        const width = 90;
        const height = 20;

        // Проверяем, скрыты ли все линии
        const allHidden = chart.data.datasets.every(ds => ds.hidden);
        const text = allHidden ? 'Показать все' : 'Скрыть все';

        ctx.save();

        // Рисуем полупрозрачный фон
        ctx.fillStyle = '#f0f4f7';
        ctx.fillRect(x, y, width, height);

        // Рисуем текст
        ctx.fillStyle = '#6c757d';
        ctx.font = '12px Roboto';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, x + width / 2, y + height / 2);

        ctx.restore();

        // Сохраняем область кнопки для обработки клика
        chart.$hideAllLabel = { x, y, width, height };
    },
    afterEvent(chart, event) {
        if (!chart.$hideAllLabel) return;

        const { x, y, width, height } = chart.$hideAllLabel;
        const { x: eventX, y: eventY } = event.event;

        // Проверяем, попал ли клик в область кнопки
        if (eventX >= x && eventX <= x + width && eventY >= y && eventY <= y + height) {
            if (event.event.type === 'click') {
                const datasets = chart.data.datasets;
                const allHidden = datasets.every(ds => ds.hidden);

                datasets.forEach(ds => (ds.hidden = !allHidden));
                chart.update();
            }
        }
    }
};

// текст перед легендами
const TextBeforeLegendsPlugin = {
    id: 'textBeforeLegends',
    afterDraw(chart, args, options) {
        if (!options || !options.text) return;

        const legend = chart.legend;
        const { ctx, chartArea } = chart;
        if (!legend || !legend.legendItems?.length || !chartArea) return;

        ctx.save();

        const text = String(options.text);
        const font = options.font || 'bold 14px Roboto';
        const color = options.color || '#36393C';
        const padding = options.padding ?? 10;

        ctx.font = font;
        ctx.fillStyle = color;

        const firstBox = legend.legendHitBoxes?.[0];
        if (!firstBox) {
            ctx.restore();
            return;
        }

        const legendLeft = firstBox.left;
        const legendTop = legend.top;
        const legendHeight = legend.height;
        const legendCenterY = legendTop + legendHeight / 2;

        const availableLeftWidth = Math.max(0, legendLeft - chartArea.left - padding);
        const singleWidth = ctx.measureText(text).width;

        // Если помещается слева от легенды — рисуем там
        if (singleWidth <= availableLeftWidth) {
            ctx.textAlign = 'right';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, legendLeft - padding, legendCenterY);
            ctx.restore();
            return;
        }

        // Если не помещается - скрываем
        ctx.restore();
    }
};

// убирает обводку у элементов легенды для всех типов графиков
const NoLegendBorderPlugin = {
    id: 'noLegendBorder',
    // применяем перед каждым draw, чтобы любые последующие пересчеты легенды
    // не возвращали обводку обратно
    beforeDraw(chart) {
        if (!chart.legend?.legendItems?.length) {
            return;
        }

        chart.legend.legendItems.forEach((item) => {
            item.lineWidth = 0;
            item.borderWidth = 0;
            item.strokeStyle = 'transparent';
        });
    },
};

/**
 * Добавляет отображение подписей с данными (data labels) непосредственно на графиках.
 * Параметры options.plugins.datalabels
 */
Chart.register(ChartDataLabels);
/**
 * Добавляет Watermark, передается props "useWatermark" в AdaptersChart, default: true.
 */
Chart.register(ChartjsPluginWatermark);
/**
 * Добавляет кнопку "Скрыть все"/"Показать все" на график.
 * Параметр options.plugins.hideAll, default: false.
 */
Chart.register(HideAll);
/**
 * Добавляет в canvas текст перед легендами, если вмещается.
 * Параметры options.plugins.textBeforeLegends:
 *  .text - обязательный параметр, соответственно, сам текст
 *  .font - настройки шрифта, default: 'bold 14px Roboto'
 *  .color - цвет текста, default: '#36393C'
 *  .padding - внутренний отступ, default: 10
 */
Chart.register(TextBeforeLegendsPlugin);
/**
 * Глобально убирает border у легенд для всех графиков.
 */
Chart.register(NoLegendBorderPlugin);
